import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { filter, uniq } from 'lodash-es'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import useResultsSelection from './useResultsSelection'

/**
 * Hook for selecting elements by type, either globally (all storeys) or for a specific storey.
 * Used by StoreyControls (global) and VisibilityFilters (storey-wise and optionally global).
 *
 * The select functions replace the selection by default; `{ extend: true }` (shift-click) merges
 * into the current selection instead.
 */
const useElementTypeSelection = () => {
  const model = useModelStore(state => state.model)
  const multiSelectElementsFloorPlan = useEditElementStore(state => state.setMultiActiveElements)
  const {
    actions: {
      multiSelectElements: multiSelectElementsStructuralPlanning,
      setMultiSelection: setMultiSelectionStructuralPlanning,
    },
    params: { storey: storeyStructuralPlanning },
  } = useStructuralPlanningSelection()
  const { setResultsSelection, addResultsSelection } = useResultsSelection()
  const location = useLocation()
  const isResultsView = location.pathname.includes('/results')
  const isStructuralPlanningView = location.pathname.includes('/structural-planning')

  // Results2 stores the surface (Decke/Dach) guid, never the slab beam guid.
  const beamGuidToSurfaceGuid = useMemo(() => {
    const map: Record<string, string> = {}
    for (const slab of [...model.vertical_slabs, ...model.vertical_roof_slabs]) {
      if (slab.beam?.guid) map[slab.beam.guid] = slab.guid
    }
    return map
  }, [model.vertical_slabs, model.vertical_roof_slabs])

  const getElementsByType = useCallback(
    (elementType: ElementTypes): string[] => {
      const elementGuids: string[] = []
      switch (elementType) {
        case 'beams':
          elementGuids.push(...model.beams.map(beam => beam.guid))
          break
        case 'columns':
          elementGuids.push(...model.columns.map(column => column.guid))
          break
        case 'slabs':
          elementGuids.push(...model.slabs.map(slab => slab.guid))
          break
        case 'vertical_slabs':
          elementGuids.push(...model.vertical_slabs.map(slab => slab.guid))
          break
        case 'outer_walls':
          elementGuids.push(
            ...model.walls.filter(wall => wall.placement === 'External').map(wall => wall.guid),
          )
          break
        case 'inner_walls':
          elementGuids.push(
            ...model.walls.filter(wall => wall.placement === 'Internal').map(wall => wall.guid),
          )
          break
        case 'foundation':
          elementGuids.push(...model.foundationElements.map(foundation => foundation.guid))
          break
        case 'roof_slabs':
          elementGuids.push(...model.roof_slabs.map(slab => slab.guid))
          break
        case 'vertical_roof_slabs':
          elementGuids.push(...model.vertical_roof_slabs.map(slab => slab.guid))
          break
        case 'purlins':
          elementGuids.push(...model.purlins.map(purlin => purlin.guid))
          break
        case 'rips':
          elementGuids.push(...model.rips.map(rip => rip.position_guid))
          break
        case 'lintels':
          elementGuids.push(...model.lintels.map(lintel => lintel.position_guid))
          break
        case 'slab_beams':
          model.vertical_slabs.forEach(slab => {
            if (slab.beam) elementGuids.push(slab.beam.guid)
          })
          break
        case 'roof_slab_beams':
          model.vertical_roof_slabs.forEach(slab => {
            if (slab.beam) elementGuids.push(slab.beam.guid)
          })
          break
        case 'standard_rips':
          elementGuids.push(...model.rips.map(rip => rip.position_guid))
          break
      }
      return elementGuids
    },
    [model],
  )

  const getElementsByTypeAndStorey = useCallback(
    (elementType: ElementTypes, storeyKey: string): string[] => {
      const elementGuids: string[] = []
      switch (elementType) {
        case 'beams':
          elementGuids.push(...filter(model.beams, ['storey', storeyKey]).map(beam => beam.guid))
          break
        case 'columns':
          elementGuids.push(
            ...filter(model.columns, ['storey', storeyKey]).map(column => column.guid),
          )
          break
        case 'slabs':
          elementGuids.push(...filter(model.slabs, ['storey', storeyKey]).map(slab => slab.guid))
          break
        case 'vertical_slabs':
          elementGuids.push(
            ...filter(model.vertical_slabs, ['storey', storeyKey]).map(slab => slab.guid),
          )
          break
        case 'outer_walls':
          elementGuids.push(
            ...model.walls
              .filter(wall => wall.storey === storeyKey && wall.placement === 'External')
              .map(wall => wall.guid),
          )
          break
        case 'inner_walls':
          elementGuids.push(
            ...model.walls
              .filter(wall => wall.storey === storeyKey && wall.placement === 'Internal')
              .map(wall => wall.guid),
          )
          break
        case 'foundation':
          elementGuids.push(
            ...model.foundationElements
              .filter(foundation => foundation.storey === storeyKey)
              .map(foundation => foundation.guid),
          )
          break
        case 'roof_slabs':
          elementGuids.push(
            ...filter(model.roof_slabs, ['storey', storeyKey]).map(slab => slab.guid),
          )
          break
        case 'vertical_roof_slabs':
          elementGuids.push(
            ...filter(model.vertical_roof_slabs, ['storey', storeyKey]).map(slab => slab.guid),
          )
          break
        case 'purlins':
          elementGuids.push(
            ...filter(model.purlins, ['storey', storeyKey]).map(purlin => purlin.guid),
          )
          break
        case 'rips':
          model.rips.forEach(rip => {
            const wall = model.walls.find(w => w.guid === rip.wall_guid)
            if (wall && wall.storey === storeyKey) {
              elementGuids.push(rip.position_guid)
            }
          })
          break
        case 'lintels':
          model.lintels.forEach(lintel => {
            const wall = model.walls.find(w => w.guid === lintel.wall_guid)
            if (wall && wall.storey === storeyKey) {
              elementGuids.push(lintel.position_guid)
            }
          })
          break
        case 'slab_beams':
          filter(model.vertical_slabs, ['storey', storeyKey]).forEach(slab => {
            if (slab.beam) elementGuids.push(slab.beam.guid)
          })
          break
        case 'roof_slab_beams':
          model.vertical_roof_slabs.forEach(slab => {
            if (slab.beam) elementGuids.push(slab.beam.guid)
          })
          break
        case 'standard_rips':
          model.rips.forEach(rip => {
            const wall = model.walls.find(w => w.guid === rip.wall_guid)
            if (wall && wall.storey === storeyKey) {
              elementGuids.push(rip.position_guid)
            }
          })
          break
      }
      return elementGuids
    },
    [model],
  )

  const applyMultiSelect = useCallback(
    (elementGuids: string[], extend?: boolean) => {
      // When extending, an empty result is a no-op; a non-extend query with no
      // matches must still clear the existing selection via the replace paths.
      if (elementGuids.length === 0 && extend) return
      // The store-backed selections merge with their current value when extending; the
      // structural planning view instead picks between its appending and replacing actions.
      const extendFloorPlan = (guids: string[]) =>
        multiSelectElementsFloorPlan(
          extend ? uniq([...useEditElementStore.getState().multiActiveElements, ...guids]) : guids,
        )
      if (isStructuralPlanningView) {
        if (storeyStructuralPlanning) {
          extendFloorPlan(elementGuids)
        } else if (extend) {
          multiSelectElementsStructuralPlanning(elementGuids)
        } else {
          setMultiSelectionStructuralPlanning(elementGuids)
        }
      } else if (isResultsView) {
        // Results2 reads selection from the selection store and stores slab/roof beams
        // by their surface guid. Replace the selection so switching storey/type clears
        // unrelated prior elements, like the other selectElementsByType* paths.
        const surfaceGuids = elementGuids.map(guid => beamGuidToSurfaceGuid[guid] ?? guid)
        if (extend) addResultsSelection(surfaceGuids)
        else setResultsSelection(surfaceGuids)
      } else {
        extendFloorPlan(elementGuids)
      }
    },
    [
      multiSelectElementsFloorPlan,
      multiSelectElementsStructuralPlanning,
      setMultiSelectionStructuralPlanning,
      setResultsSelection,
      addResultsSelection,
      beamGuidToSurfaceGuid,
      isStructuralPlanningView,
      isResultsView,
      storeyStructuralPlanning,
    ],
  )

  /** Select all elements of the given type (global, all storeys). */
  const selectElementsByType = useCallback(
    (elementType: ElementTypes, options?: { extend?: boolean }) => {
      const elementGuids = getElementsByType(elementType)
      applyMultiSelect(elementGuids, options?.extend)
    },
    [getElementsByType, applyMultiSelect],
  )

  /** Select all elements of the given type in the given storey. */
  const selectElementsByTypeAndStorey = useCallback(
    (elementType: ElementTypes, storeyKey: string, options?: { extend?: boolean }) => {
      const elementGuids = getElementsByTypeAndStorey(elementType, storeyKey)
      applyMultiSelect(elementGuids, options?.extend)
    },
    [getElementsByTypeAndStorey, applyMultiSelect],
  )

  return {
    getElementsByType,
    getElementsByTypeAndStorey,
    selectElementsByType,
    selectElementsByTypeAndStorey,
  }
}

export default useElementTypeSelection
